import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"
import Sortable from "sortablejs"

// Connects to data-controller="column-selector-panel"
//
// Two-panel drag-and-drop interface for managing column visibility and order
// Modeled after report editor pattern with properties panel for column configuration.
//
// Features:
// - Drag from Available → Visible to add column
// - Drag from Visible → Available to remove column (with visual feedback)
// - Reorder within Visible panel
// - Button clicks for add/remove as alternative
// - Properties panel for configuring column parameters (data_point, date, etc.)
// - Duplicate column functionality for multiple instances
// - Auto-save to backend via AJAX
// - Search functionality (via column-search-v3 controller)
//
export default class extends Controller {
  static targets = [
    "availableList",
    "visibleList",
    "emptyPlaceholder",
    "availableGroup",
    "availablePanel",  // The entire left panel (for drop zone styling)
    "propertiesPanel",  // Properties panel container
    "propertiesContent",  // Properties panel content area
    "propertiesPanelTitle",  // Properties panel title
    "templates"  // Hidden templates container
  ]

  static values = {
    saveUrl: String,  // AJAX endpoint for saving column order
    updateParametersUrl: String  // AJAX endpoint for updating column parameters
  }

  // Currently selected column ref
  selectedColumnRef = null

  connect() {
    logger.log('[Column Selector Panel] Connected', {
      saveUrl: this.saveUrlValue,
      availableLists: this.availableListTargets.length,
      visibleColumns: this.visibleListTarget.querySelectorAll('.column-item').length
    })

    this.initializeSortable()
    this.updateGroupVisibility()
  }

  disconnect() {
    // Destroy all sortable instances
    if (this.availableSortables) {
      this.availableSortables.forEach(sortable => sortable.destroy())
    }
    if (this.visibleSortable) {
      this.visibleSortable.destroy()
    }
  }

  initializeSortable() {
    logger.log('[Column Selector Panel] Initializing sortable for', this.availableListTargets.length, 'available lists')

    // Initialize sortable for each "Available" group
    this.availableSortables = this.availableListTargets.map((availableList, index) => {
      logger.log('[Column Selector Panel] Creating sortable for list', index, availableList)
      return Sortable.create(availableList, {
        group: {
          name: 'columns',
          pull: true,   // Can drag to Visible
          put: false    // Don't accept drops into specific spots - use drop zone instead
        },
        sort: false,      // Can't reorder within Available
        animation: 150,
        handle: '.drag-handle',
        draggable: '.column-item',
        dragClass: 'sortable-drag',
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',

        // Auto scroll support
        scroll: true,
        scrollSensitivity: 100,
        scrollSpeed: 20,
        bubbleScroll: true,

        onStart: (evt) => {
          logger.log('[Column Selector Panel] Drag started from Available list', index)
        },

        onRemove: (evt) => {
          logger.log('[Column Selector Panel] onRemove triggered on Available list', index, evt)
          this.handleRemoveFromAvailable(evt)
        }
      })
    })

    // Initialize sortable for "Visible" panel
    logger.log('[Column Selector Panel] Creating sortable for Visible list')
    this.visibleSortable = Sortable.create(this.visibleListTarget, {
      group: {
        name: 'columns',
        pull: true,  // Can drag out to remove
        put: true    // Can accept items from Available
      },
      sort: true,    // Can reorder
      animation: 150,
      handle: '.drag-handle',
      draggable: '.column-item',
      dragClass: 'sortable-drag',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',

      // Auto scroll support
      scroll: true,
      scrollSensitivity: 100,
      scrollSpeed: 20,
      bubbleScroll: true,

      onStart: (evt) => {
        logger.log('[Column Selector Panel] Drag started from Visible', evt)
        // Enable drop zone on hover
        this.enableDropZoneHover()
      },

      onEnd: (evt) => {
        logger.log('[Column Selector Panel] Drag ended from Visible', evt)
        // Disable drop zone hover and hide if visible
        this.disableDropZoneHover()
      },

      onAdd: (evt) => {
        logger.log('[Column Selector Panel] onAdd triggered on Visible', evt)
        this.handleAdd(evt)
      },

      onUpdate: (evt) => {
        logger.log('[Column Selector Panel] onUpdate triggered on Visible', evt)
        this.handleUpdate(evt)
      },

      onRemove: (evt) => {
        logger.log('[Column Selector Panel] onRemove triggered on Visible', evt)
        this.handleRemove(evt)
      }
    })
  }

  // Enable drop zone on hover (when dragging from Visible)
  enableDropZoneHover() {
    logger.log('[Column Selector Panel] Enabling drop zone hover')
    if (this.hasAvailablePanelTarget) {
      // Store bound functions so we can remove them later
      this.boundHandleDragOver = this.handleDragOver.bind(this)
      this.boundHandleDrop = this.handleDrop.bind(this)
      this.boundHandleDragEnter = this.handleDragEnter.bind(this)
      this.boundHandleDragLeave = this.handleDragLeave.bind(this)

      // Make the entire panel accept drops
      this.availablePanelTarget.addEventListener('dragover', this.boundHandleDragOver)
      this.availablePanelTarget.addEventListener('drop', this.boundHandleDrop)
      this.availablePanelTarget.addEventListener('dragenter', this.boundHandleDragEnter)
      this.availablePanelTarget.addEventListener('dragleave', this.boundHandleDragLeave)
    }
  }

  // Disable drop zone hover
  disableDropZoneHover() {
    logger.log('[Column Selector Panel] Disabling drop zone hover')
    if (this.hasAvailablePanelTarget) {
      // Remove event listeners
      this.availablePanelTarget.removeEventListener('dragover', this.boundHandleDragOver)
      this.availablePanelTarget.removeEventListener('drop', this.boundHandleDrop)
      this.availablePanelTarget.removeEventListener('dragenter', this.boundHandleDragEnter)
      this.availablePanelTarget.removeEventListener('dragleave', this.boundHandleDragLeave)

      // Remove the visual indicator if it's still showing
      this.availablePanelTarget.classList.remove('drop-zone-active')
    }
  }

  // Handle drag enter - show drop zone
  handleDragEnter(event) {
    event.preventDefault()
    logger.log('[Column Selector Panel] Drag entered Available panel')
    if (this.hasAvailablePanelTarget) {
      this.availablePanelTarget.classList.add('drop-zone-active')
    }
  }

  // Handle drag leave - hide drop zone
  handleDragLeave(event) {
    // Only hide if we're actually leaving the panel (not entering a child)
    if (event.target === this.availablePanelTarget) {
      logger.log('[Column Selector Panel] Drag left Available panel')
      if (this.hasAvailablePanelTarget) {
        this.availablePanelTarget.classList.remove('drop-zone-active')
      }
    }
  }

  // Handle dragover event on Available panel
  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    logger.log('[Column Selector Panel] Drag over Available panel')
  }

  // Handle drop event on Available panel
  // For duplicable columns (hasParams), just remove (they stay in available)
  // For regular columns, move back to available list
  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    logger.log('[Column Selector Panel] Dropped on Available panel', event)

    // Get the currently dragged item from SortableJS
    const draggedItem = this.visibleSortable.el.querySelector('.sortable-ghost') ||
                        this.visibleSortable.el.querySelector('.sortable-drag')

    if (!draggedItem) {
      logger.warn('[Column Selector Panel] No dragged item found')
      return
    }

    const columnName = draggedItem.dataset.columnName
    const hasParams = draggedItem.dataset.hasParameters === 'true'
    logger.log('[Column Selector Panel] Removing column from Visible via drag:', columnName, 'hasParams:', hasParams)

    if (hasParams) {
      // Duplicable columns: just remove (original still in available)
      draggedItem.remove()
    } else {
      // Regular columns: move back to available list
      this.moveColumnBackToAvailable(draggedItem, columnName)
    }

    // Update UI and save
    this.updateGroupVisibility()
    this.saveOrder()
  }

  // Handle adding column to Visible panel (dragged from Available)
  // For duplicable columns (hasParams), clone item back to Available
  // For regular columns, item was moved so no clone needed
  handleAdd(evt) {
    const item = evt.item
    const columnName = item.dataset.columnName
    const hasParams = item.dataset.hasParameters === 'true'
    const originalGroup = item.dataset.columnGroup

    logger.log('[Column Selector Panel] Column added to Visible:', columnName, 'hasParams:', hasParams)

    // Generate a unique column_ref for this instance
    const columnRef = `${columnName}_${Date.now()}`
    item.dataset.columnRef = columnRef
    item.dataset.currentParameters = '{}'

    // Transform item from Available (with add button) to Visible (with remove button)
    // Remove add button if present
    const addButton = item.querySelector('.add-button')
    if (addButton) {
      addButton.remove()
    }

    // Remove existing gear icon (will be re-added as config indicator)
    const existingGear = item.querySelector('span.text-muted.small:not(.config-indicator)')
    if (existingGear && hasParams) existingGear.remove()

    // Create actions container
    const actionsDiv = document.createElement('div')
    actionsDiv.className = 'column-item-actions ms-auto'

    // Add duplicate button and config indicator if column has parameters
    if (hasParams) {
      const duplicateButton = document.createElement('button')
      duplicateButton.type = 'button'
      duplicateButton.className = 'duplicate-button me-1'
      duplicateButton.setAttribute('data-action', 'click->column-selector-panel#duplicateColumn:stop')
      duplicateButton.setAttribute('data-column-selector-panel-column-ref-param', columnRef)
      duplicateButton.setAttribute('title', 'שכפל עמודה')
      duplicateButton.innerHTML = '<i class="fa fa-copy"></i>'
      actionsDiv.appendChild(duplicateButton)

      // Add click action to open properties panel
      item.setAttribute('data-action', 'click->column-selector-panel#selectColumn')

      // Add config indicator (gear icon) before actions
      const configIndicator = document.createElement('span')
      configIndicator.className = 'ms-1 text-muted small config-indicator'
      configIndicator.setAttribute('title', 'לחץ להגדרות')
      configIndicator.innerHTML = '<i class="fa fa-cog fa-xs"></i>'
      const columnNameSpan = item.querySelector('.column-name')
      if (columnNameSpan) {
        columnNameSpan.after(configIndicator)
      }
    }

    // Add remove button
    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'remove-button'
    removeButton.setAttribute('data-action', 'click->column-selector-panel#removeColumn:stop')
    removeButton.setAttribute('data-column-name', columnName)
    removeButton.setAttribute('data-column-ref', columnRef)
    removeButton.setAttribute('title', 'הסר עמודה')
    removeButton.innerHTML = '<i class="fa fa-times"></i>'
    actionsDiv.appendChild(removeButton)

    // Remove any existing actions container
    const existingActions = item.querySelector('.column-item-actions')
    if (existingActions) existingActions.remove()

    item.appendChild(actionsDiv)

    // Add hidden fields for parameters
    if (hasParams) {
      // Remove any existing hidden fields
      item.querySelectorAll('input[data-parameter]').forEach(el => el.remove())

      try {
        const requiredParams = JSON.parse(item.dataset.requiredParameters || '{}')
        for (const [paramName, _] of Object.entries(requiredParams)) {
          const hiddenField = document.createElement('input')
          hiddenField.type = 'hidden'
          hiddenField.dataset.parameter = paramName
          hiddenField.value = ''
          item.appendChild(hiddenField)
        }
      } catch (e) {
        logger.error('[Column Selector Panel] Error adding hidden fields:', e)
      }
    }

    // Update classes
    item.classList.add('visible-column-item')
    item.classList.remove('available-column-item')

    // For duplicable columns (hasParams), clone the item back to Available list
    // For regular columns, the item was moved so nothing to clone back
    if (hasParams && originalGroup) {
      const targetList = this.availableListTargets.find(list => list.dataset.groupName === originalGroup)
      if (targetList) {
        // Create a new available item (clone of original structure)
        const availableClone = document.createElement('div')
        availableClone.className = 'column-item available-column-item'
        availableClone.dataset.columnSearchV3Target = 'item'
        availableClone.dataset.columnSearchNameValue = item.dataset.columnLabel
        availableClone.dataset.columnName = columnName
        availableClone.dataset.columnLabel = item.dataset.columnLabel
        availableClone.dataset.columnGroup = originalGroup
        availableClone.dataset.columnRef = `${columnName}_${Date.now() + 1}`
        availableClone.dataset.requiredParameters = item.dataset.requiredParameters
        availableClone.dataset.hasParameters = 'true'

        availableClone.innerHTML = `
          <i class="fa fa-grip-vertical drag-handle me-2"></i>
          <span class="column-name">${item.dataset.columnLabel}</span>
          <span class="ms-1 text-muted small" title="יש הגדרות נוספות">
            <i class="fa fa-cog fa-xs opacity-50"></i>
          </span>
          <button type="button" class="add-button ms-auto"
            data-action="click->column-selector-panel#addColumn:stop"
            data-column-name="${columnName}"
            title="הוסף עמודה">
            <i class="fa fa-plus"></i>
          </button>
        `
        targetList.appendChild(availableClone)
        logger.log('[Column Selector Panel] Cloned duplicable column back to Available:', columnName)
      }
    }

    // Update empty groups visibility
    this.updateGroupVisibility()

    // Save changes
    this.saveOrder()
  }


  // Handle item being removed from Available panel (dragged to Visible)
  handleRemoveFromAvailable(evt) {
    const columnName = evt.item.dataset.columnName
    logger.log('[Column Selector Panel] Column removed from Available:', columnName)

    // Update empty groups visibility
    this.updateGroupVisibility()
    // Note: saveOrder() will be called by handleAdd on the Visible panel
  }

  // Handle reordering within Visible panel
  handleUpdate(evt) {
    logger.log('[Column Selector Panel] Columns reordered in Visible panel')

    // Save changes
    this.saveOrder()
  }

  // Handle removing column from Visible panel (dragged back to Available)
  handleRemove(evt) {
    const item = evt.item
    const columnName = item.dataset.columnName

    logger.log('[Column Selector Panel] Column removed from Visible:', columnName)

    // Update empty groups visibility happens in handleAddToAvailable
    // saveOrder() will be called there too
  }

  // Remove column via button click
  // For duplicable columns (hasParams), just remove (they stay in available)
  // For regular columns, move back to available list
  removeColumn(event) {
    const button = event.currentTarget
    const columnName = button.dataset.columnName
    const item = button.closest('.column-item')

    if (item) {
      const hasParams = item.dataset.hasParameters === 'true'
      logger.log('[Column Selector Panel] Removing column via button:', columnName, 'hasParams:', hasParams)

      if (hasParams) {
        // Duplicable columns: just remove (original still in available)
        item.remove()
      } else {
        // Regular columns: move back to available list
        this.moveColumnBackToAvailable(item, columnName)
      }

      // Update empty groups visibility
      this.updateGroupVisibility()

      // Save changes
      this.saveOrder()
    }
  }

  // Helper: Move a regular column back to the available list
  moveColumnBackToAvailable(item, columnName) {
    const columnGroup = item.dataset.columnGroup

    // Remove actions container and add back add button
    const actionsDiv = item.querySelector('.column-item-actions')
    if (actionsDiv) actionsDiv.remove()

    // Add add button
    const addButton = document.createElement('button')
    addButton.type = 'button'
    addButton.className = 'add-button ms-auto'
    addButton.setAttribute('data-action', 'click->column-selector-panel#addColumn:stop')
    addButton.setAttribute('data-column-name', columnName)
    addButton.setAttribute('title', 'הוסף עמודה')
    addButton.innerHTML = '<i class="fa fa-plus"></i>'
    item.appendChild(addButton)

    // Update classes
    item.classList.add('available-column-item')
    item.classList.remove('visible-column-item')

    // Remove click action for properties panel
    item.removeAttribute('data-action')

    // Find the correct Available list to return item to
    let targetList = this.availableListTargets.find(list => list.dataset.groupName === columnGroup)

    // Fallback to first list if group not found
    if (!targetList && this.availableListTargets.length > 0) {
      logger.warn('[Column Selector Panel] Original group not found for', columnName, ', using first available list')
      targetList = this.availableListTargets[0]
    }

    // Move item back to Available list
    if (targetList) {
      logger.log('[Column Selector Panel] Returning column to group:', targetList.dataset.groupName)
      targetList.appendChild(item)

      // Expand the group so user can see the returned column
      const collapseElement = targetList.closest('.collapse')
      if (collapseElement && !collapseElement.classList.contains('show')) {
        const bsCollapse = new bootstrap.Collapse(collapseElement, { toggle: true })
      }
    }
  }

  // Add column via + button click
  // For duplicable columns (hasParams), clone the item so it stays in the available list
  // For regular columns, move the item to visible
  addColumn(event) {
    const button = event.currentTarget
    const columnName = button.dataset.columnName
    const sourceItem = button.closest('.column-item')

    if (sourceItem) {
      logger.log('[Column Selector Panel] Adding column via button:', columnName)

      // Check if column has parameters (duplicable)
      const hasParams = sourceItem.dataset.hasParameters === 'true'

      // For duplicable columns, clone the item; for regular columns, move it
      let visibleItem
      if (hasParams) {
        // Clone the source item for the visible list
        visibleItem = sourceItem.cloneNode(true)
        // Generate a new column_ref for available item too (so each add creates unique ref)
        sourceItem.dataset.columnRef = `${columnName}_${Date.now() + 1}`
      } else {
        // Regular columns: move the item
        visibleItem = sourceItem
      }

      // Generate a new column_ref for this instance
      const columnRef = `${columnName}_${Date.now()}`
      visibleItem.dataset.columnRef = columnRef
      visibleItem.dataset.currentParameters = '{}'

      // Remove add button from visible item
      const addBtn = visibleItem.querySelector('.add-button')
      if (addBtn) addBtn.remove()

      // Remove gear icon from visible item (will be re-added as config indicator)
      const gearIcon = visibleItem.querySelector('.config-indicator, span.text-muted.small')
      if (gearIcon && hasParams) gearIcon.remove()

      // Create actions container
      const actionsDiv = document.createElement('div')
      actionsDiv.className = 'column-item-actions ms-auto'

      // Add duplicate button if column has parameters
      if (hasParams) {
        const duplicateButton = document.createElement('button')
        duplicateButton.type = 'button'
        duplicateButton.className = 'duplicate-button me-1'
        duplicateButton.setAttribute('data-action', 'click->column-selector-panel#duplicateColumn:stop')
        duplicateButton.setAttribute('data-column-selector-panel-column-ref-param', columnRef)
        duplicateButton.setAttribute('title', 'שכפל עמודה')
        duplicateButton.innerHTML = '<i class="fa fa-copy"></i>'
        actionsDiv.appendChild(duplicateButton)

        // Add click action to open properties panel
        visibleItem.setAttribute('data-action', 'click->column-selector-panel#selectColumn')

        // Add config indicator (gear icon) before actions
        const configIndicator = document.createElement('span')
        configIndicator.className = 'ms-1 text-muted small config-indicator'
        configIndicator.setAttribute('title', 'לחץ להגדרות')
        configIndicator.innerHTML = '<i class="fa fa-cog fa-xs"></i>'
        const columnNameSpan = visibleItem.querySelector('.column-name')
        if (columnNameSpan) {
          columnNameSpan.after(configIndicator)
        }
      }

      // Add remove button
      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'remove-button'
      removeButton.setAttribute('data-action', 'click->column-selector-panel#removeColumn:stop')
      removeButton.setAttribute('data-column-name', columnName)
      removeButton.setAttribute('data-column-ref', columnRef)
      removeButton.setAttribute('title', 'הסר עמודה')
      removeButton.innerHTML = '<i class="fa fa-times"></i>'
      actionsDiv.appendChild(removeButton)

      // Remove any existing actions container from cloned item
      const existingActions = visibleItem.querySelector('.column-item-actions')
      if (existingActions) existingActions.remove()

      visibleItem.appendChild(actionsDiv)

      // Add hidden fields for parameters if column has them
      if (hasParams) {
        // Remove any existing hidden fields (from clone)
        visibleItem.querySelectorAll('input[data-parameter]').forEach(el => el.remove())

        try {
          const requiredParams = JSON.parse(visibleItem.dataset.requiredParameters || '{}')
          for (const [paramName, _] of Object.entries(requiredParams)) {
            const hiddenField = document.createElement('input')
            hiddenField.type = 'hidden'
            hiddenField.dataset.parameter = paramName
            hiddenField.value = ''
            visibleItem.appendChild(hiddenField)
          }
        } catch (e) {
          logger.error('[Column Selector Panel] Error adding hidden fields:', e)
        }
      }

      // Update classes
      visibleItem.classList.add('visible-column-item')
      visibleItem.classList.remove('available-column-item')

      // Move/add item to visible list
      this.visibleListTarget.appendChild(visibleItem)

      // Update empty groups visibility
      this.updateGroupVisibility()

      // Save changes
      this.saveOrder()
    }
  }

  // Update visibility of groups based on whether they have available columns
  updateGroupVisibility() {
    if (!this.hasAvailableGroupTarget) return

    this.availableGroupTargets.forEach(group => {
      // Find all column items in this group's available list
      const groupItems = group.querySelector('[data-column-selector-panel-target="availableList"]')
      if (!groupItems) return

      const availableItems = groupItems.querySelectorAll('.column-item.available-column-item')

      // Style group based on whether it has available columns
      // Don't hide completely - keep in DOM to preserve position
      if (availableItems.length === 0) {
        group.classList.add('group-empty')
        group.classList.remove('group-has-items')
      } else {
        group.classList.remove('group-empty')
        group.classList.add('group-has-items')
      }
    })
  }

  // Save column order to backend using new format with column_config
  async saveOrder() {
    logger.log('[Column Selector Panel] saveOrder called')

    // Get current order from Visible panel
    const visibleItems = this.visibleListTarget.querySelectorAll('.visible-column-item')
    logger.log('[Column Selector Panel] Found visible items:', visibleItems.length)

    // Build column_config with column_ref keys and parameter values
    const columnConfig = {}
    const columnOrder = []

    // Process visible columns
    visibleItems.forEach(item => {
      const columnRef = item.dataset.columnRef
      const columnName = item.dataset.columnName

      columnOrder.push(columnRef)

      // Parse current parameters
      let currentParams = {}
      try {
        currentParams = JSON.parse(item.dataset.currentParameters || '{}')
      } catch (e) {}

      columnConfig[columnRef] = {
        column_name: columnName,
        visible: true,
        ...currentParams
      }
    })

    // Process available (non-visible) columns - they don't need to be tracked
    // since they're not visible, but we could track them if needed

    logger.log('[Column Selector Panel] Saving column_config:', columnConfig)
    logger.log('[Column Selector Panel] Saving column_order:', columnOrder)
    logger.log('[Column Selector Panel] Save URL:', this.saveUrlValue)

    // Update counts
    this.updateCounts()

    // Update empty state
    this.updateEmptyState()

    // Save to backend via AJAX
    if (!this.saveUrlValue) {
      logger.warn('[Column Selector Panel] No save URL provided, skipping save')
      return
    }

    try {
      const requestBody = {
        column_config: columnConfig,
        column_order: columnOrder
      }

      logger.log('[Column Selector Panel] Sending POST request to:', this.saveUrlValue)
      logger.log('[Column Selector Panel] Request body:', requestBody)

      const response = await fetch(this.saveUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      })

      logger.log('[Column Selector Panel] Response status:', response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      logger.log('[Column Selector Panel] Column config saved successfully:', data)

      // Reload table to reflect new column order
      this.reloadTable()
    } catch (error) {
      console.error('[Column Selector Panel] Error saving column config:', error)
      // TODO: Show user-friendly error message
    }
  }

  // Update column counts in panel headers
  updateCounts() {
    if (this.hasVisibleCountTarget) {
      const visibleCount = this.visibleListTarget.querySelectorAll('.visible-column-item').length
      this.visibleCountTarget.textContent = visibleCount
    }

    if (this.hasAvailableCountTarget) {
      // Count available columns across all groups
      const availableCount = this.availableListTargets.reduce((total, list) => {
        return total + list.querySelectorAll('.available-column-item').length
      }, 0)
      this.availableCountTarget.textContent = availableCount
    }
  }

  // Update empty state visibility
  updateEmptyState() {
    if (!this.hasEmptyPlaceholderTarget) return

    const visibleCount = this.visibleListTarget.querySelectorAll('.visible-column-item').length

    if (visibleCount === 0) {
      this.emptyPlaceholderTarget.classList.remove('d-none')
    } else {
      this.emptyPlaceholderTarget.classList.add('d-none')
    }
  }

  // Reload data table to reflect new column order
  reloadTable() {
    // Find data-table controller in the page
    const dataTableElement = document.querySelector('[data-controller~="data-table"]')
    if (!dataTableElement) {
      logger.warn('[Column Selector Panel] No data table found to reload')
      return
    }

    // Get the data-table controller instance
    const dataTableController = this.application.getControllerForElementAndIdentifier(
      dataTableElement,
      'data-table'
    )

    if (dataTableController && typeof dataTableController.loadData === 'function') {
      logger.log('[Column Selector Panel] Reloading data table with new column order')

      // Clear filters before reloading - column indices change when columns are added/removed,
      // so existing filters would apply to wrong columns
      if (typeof dataTableController.resetFilters === 'function') {
        logger.log('[Column Selector Panel] Clearing filters due to column changes')
        dataTableController.resetFilters()
      }

      // Pass reloadHeaders flag to rebuild thead with new column order
      dataTableController.loadData({ reloadHeaders: true })
    } else {
      logger.warn('[Column Selector Panel] Data table controller not found or missing loadData method')
    }
  }

  // Reset to default column order (clear saved settings)
  async resetToDefault() {
    logger.log('[Column Selector Panel] Resetting to default column order')

    // Clear saved settings by sending empty array
    if (!this.saveUrlValue) {
      logger.warn('[Column Selector Panel] No save URL provided, skipping reset')
      return
    }

    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ column_order: [] })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      logger.log('[Column Selector Panel] Reset to default successfully:', data)

      // Settings deleted on server - reload page to show defaults
      if (data.reset) {
        logger.log('[Column Selector Panel] Reloading page to show default columns')
        this.close()
        window.location.reload()
      } else {
        // Fallback to reload
        window.location.reload()
      }
    } catch (error) {
      console.error('[Column Selector Panel] Error resetting to default:', error)
      // TODO: Show user-friendly error message
    }
  }

  // Close the column selector panel
  close() {
    // If in modal, close via Bootstrap Modal API
    if (this.element.classList.contains('modal')) {
      const modal = bootstrap.Modal.getInstance(this.element)
      if (modal) {
        modal.hide()
      }
    } else {
      // Emit event for parent controller to handle closing
      this.element.dispatchEvent(new CustomEvent('column-selector:close', {
        bubbles: true,
        detail: { source: 'panel' }
      }))
    }
  }

  // ========================================================================
  // Properties Panel Methods
  // ========================================================================

  // Select a column and open the properties panel
  selectColumn(event) {
    // Don't open panel if clicking on buttons
    if (event.target.closest('button')) return

    const item = event.currentTarget
    const columnRef = item.dataset.columnRef
    const hasParams = item.dataset.hasParameters === 'true'

    if (!hasParams) {
      logger.log('[Column Selector Panel] Column has no parameters, skipping properties panel')
      return
    }

    logger.log('[Column Selector Panel] Selecting column:', columnRef)

    // Deselect all, select this one
    this.visibleListTarget.querySelectorAll('.visible-column-item').forEach(el => {
      el.classList.remove('selected')
    })
    item.classList.add('selected')

    this.selectedColumnRef = columnRef

    // Load properties
    this.loadColumnProperties(item)
    this.openPropertiesPanel()
  }

  // Open the properties panel
  openPropertiesPanel() {
    if (this.hasPropertiesPanelTarget) {
      this.propertiesPanelTarget.classList.add('open')
      // Expand modal to accommodate panel
      const modalDialog = this.element.querySelector('.modal-dialog')
      if (modalDialog) {
        modalDialog.classList.add('properties-open')
      }
    }
  }

  // Close the properties panel
  closePropertiesPanel() {
    if (this.hasPropertiesPanelTarget) {
      this.propertiesPanelTarget.classList.remove('open')
      // Reset modal width
      const modalDialog = this.element.querySelector('.modal-dialog')
      if (modalDialog) {
        modalDialog.classList.remove('properties-open')
      }
    }
    // Deselect column
    this.visibleListTarget.querySelectorAll('.visible-column-item.selected').forEach(el => {
      el.classList.remove('selected')
    })
    this.selectedColumnRef = null
  }

  // Load column properties into the properties panel
  loadColumnProperties(columnElement) {
    const columnRef = columnElement.dataset.columnRef
    const columnLabel = columnElement.dataset.columnLabel
    let requiredParams = {}
    let currentParams = {}

    try {
      requiredParams = JSON.parse(columnElement.dataset.requiredParameters || '{}')
      currentParams = JSON.parse(columnElement.dataset.currentParameters || '{}')
    } catch (e) {
      logger.error('[Column Selector Panel] Error parsing parameters:', e)
    }

    logger.log('[Column Selector Panel] Loading properties for:', columnRef, requiredParams, currentParams)

    // Update panel title
    if (this.hasPropertiesPanelTitleTarget) {
      this.propertiesPanelTitleTarget.textContent = columnLabel
    }

    // Build properties form
    const html = this.buildPropertiesForm(requiredParams, currentParams, columnRef)
    if (this.hasPropertiesContentTarget) {
      this.propertiesContentTarget.innerHTML = html
    }
  }

  // Build the properties form HTML from required parameters
  buildPropertiesForm(requiredParams, currentParams, columnRef) {
    if (!requiredParams || Object.keys(requiredParams).length === 0) {
      return `<div class="text-center text-muted py-4">
        <i class="fa fa-check-circle fa-2x mb-2 opacity-50"></i>
        <p class="mb-0">אין הגדרות נוספות לעמודה זו</p>
      </div>`
    }

    let html = '<form class="column-properties-form">'

    for (const [paramName, paramConfig] of Object.entries(requiredParams)) {
      const currentValue = currentParams[paramName] || ''
      const paramType = typeof paramConfig === 'object' ? (paramConfig.type || 'text') : paramConfig

      // Try to use a template for this parameter
      const template = this.getParameterTemplate(paramName, paramType)
      if (template) {
        html += this.populateTemplate(template, paramName, currentValue, columnRef, paramConfig)
      } else {
        // Generate default input
        html += this.generateDefaultInput(paramName, paramType, currentValue, columnRef, paramConfig)
      }
    }

    html += '</form>'
    return html
  }

  // Get a template for a specific parameter
  getParameterTemplate(paramName, paramType) {
    if (!this.hasTemplatesTarget) return null

    // Try specific template first (e.g., #property-template-data_point)
    let template = this.templatesTarget.querySelector(`#property-template-${paramName}`)
    if (template) return template.innerHTML

    // Fall back to type-based template (e.g., #property-template-select)
    template = this.templatesTarget.querySelector(`#property-template-${paramType}`)
    if (template) return template.innerHTML

    return null
  }

  // Populate a template with values
  populateTemplate(templateHtml, paramName, currentValue, columnRef, paramConfig) {
    // Create a temporary container to manipulate the template
    const container = document.createElement('div')
    container.innerHTML = templateHtml

    // Set data attributes
    const inputs = container.querySelectorAll('[data-parameter-name]')
    inputs.forEach(input => {
      input.dataset.parameterName = paramName
      input.dataset.columnRef = columnRef

      // Set current value
      if (input.tagName === 'SELECT') {
        // If select is empty and paramConfig has options, populate them dynamically
        if (input.options.length === 0 && paramConfig && paramConfig.options && Array.isArray(paramConfig.options)) {
          paramConfig.options.forEach(opt => {
            const option = document.createElement('option')
            option.value = opt
            option.textContent = this.getOptionLabel(paramName, opt)
            if (opt.toString() === currentValue.toString()) {
              option.setAttribute('selected', 'selected')
            }
            input.appendChild(option)
          })
        } else {
          // Template has pre-defined options, remove 'selected' attr from all, then add to current value
          // Note: Must use setAttribute/removeAttribute because .selected property doesn't persist in innerHTML
          Array.from(input.options).forEach(opt => opt.removeAttribute('selected'))
          const option = input.querySelector(`option[value="${currentValue}"]`)
          if (option) {
            option.setAttribute('selected', 'selected')
          } else if (currentValue && currentValue !== '') {
            logger.warn('[Column Selector Panel] No option found for value:', currentValue, 'in', paramName)
          }
        }
      } else {
        input.value = currentValue || ''
      }
    })

    // Update labels if using generic template
    const labels = container.querySelectorAll('[data-template-label]')
    labels.forEach(label => {
      label.textContent = this.getParameterLabel(paramName)
    })

    return container.innerHTML
  }

  // Generate a default input for unknown parameter types
  generateDefaultInput(paramName, paramType, currentValue, columnRef, paramConfig) {
    const label = this.getParameterLabel(paramName)
    let inputHtml = ''

    if (paramType === 'select' && paramConfig.options) {
      const options = paramConfig.options.map(opt => {
        const selected = opt.toString() === currentValue.toString() ? 'selected' : ''
        const optLabel = this.getOptionLabel(paramName, opt)
        return `<option value="${opt}" ${selected}>${optLabel}</option>`
      }).join('')

      inputHtml = `<select class="form-select"
        data-parameter-name="${paramName}"
        data-column-ref="${columnRef}"
        data-action="change->column-selector-panel#updateParameter">${options}</select>`
    } else if (paramType === 'date') {
      inputHtml = `<input type="text" class="form-control"
        placeholder="dd-mm-yyyy"
        value="${currentValue}"
        data-parameter-name="${paramName}"
        data-column-ref="${columnRef}"
        data-action="change->column-selector-panel#updateParameter">`
    } else {
      inputHtml = `<input type="text" class="form-control"
        value="${currentValue}"
        data-parameter-name="${paramName}"
        data-column-ref="${columnRef}"
        data-action="change->column-selector-panel#updateParameter">`
    }

    return `<div class="form-group mb-3">
      <label class="form-label">${label}</label>
      ${inputHtml}
    </div>`
  }

  // Get localized label for a parameter
  getParameterLabel(paramName) {
    const labels = {
      data_point: 'סוג נתון',
      date: 'תאריך',
      user_data_point: 'פרט משתמש',
      employee_data_point: 'פרט עובד'
    }
    return labels[paramName] || paramName
  }

  // Get localized label for an option
  getOptionLabel(paramName, optionValue) {
    const optionLabels = {
      data_point: {
        value: 'ערך',
        updated_at: 'תאריך עדכון',
        updated_by: 'עודכן על ידי',
        update_context: 'הקשר עדכון',
        history: 'היסטוריה'
      },
      user_data_point: {
        name: 'שם',
        phone_number: 'טלפון',
        email: 'דוא״ל',
        full_info: 'מידע מלא'
      },
      employee_data_point: {
        full_name: 'שם מלא',
        first_name: 'שם פרטי',
        last_name: 'שם משפחה',
        phone: 'טלפון',
        email: 'דוא״ל',
        id_number: 'תעודת זהות',
        start_date: 'תאריך התחלה',
        end_date: 'תאריך סיום',
        full_info: 'מידע מלא'
      }
    }
    return optionLabels[paramName]?.[optionValue] || optionValue
  }

  // Update a parameter value
  updateParameter(event) {
    const input = event.target
    const paramName = input.dataset.parameterName
    const columnRef = input.dataset.columnRef
    const value = input.value

    logger.log('[Column Selector Panel] Updating parameter:', paramName, '=', value, 'for', columnRef)

    // Find the column item
    const columnItem = this.visibleListTarget.querySelector(`[data-column-ref="${columnRef}"]`)
    if (!columnItem) {
      logger.error('[Column Selector Panel] Column item not found:', columnRef)
      return
    }

    // Update hidden field
    const hiddenField = columnItem.querySelector(`[data-parameter="${paramName}"]`)
    if (hiddenField) {
      hiddenField.value = value
    }

    // Update currentParameters data attribute
    let currentParams = {}
    try {
      currentParams = JSON.parse(columnItem.dataset.currentParameters || '{}')
    } catch (e) {}
    currentParams[paramName] = value
    columnItem.dataset.currentParameters = JSON.stringify(currentParams)

    // Update display label if data_point changed
    if (paramName === 'data_point' || paramName === 'user_data_point' || paramName === 'employee_data_point') {
      this.updateColumnDisplayLabel(columnItem, currentParams)
    }

    // Save to backend
    this.saveOrder()
  }

  // Update the column display label based on current parameters
  updateColumnDisplayLabel(columnItem, params) {
    const baseLabel = columnItem.dataset.columnLabel
    const labelSpan = columnItem.querySelector('.column-name')
    if (!labelSpan) return

    let displayLabel = baseLabel
    const dataPoint = params.data_point || params.user_data_point || params.employee_data_point

    // Skip suffix for default values: 'value' (data_point), 'name' (user), 'full_name' (employee)
    if (dataPoint && dataPoint !== 'value' && dataPoint !== 'name' && dataPoint !== 'full_name') {
      let paramType = 'data_point'
      if (params.user_data_point) paramType = 'user_data_point'
      else if (params.employee_data_point) paramType = 'employee_data_point'

      const dataPointLabel = this.getOptionLabel(paramType, dataPoint)
      displayLabel = `${baseLabel} (${dataPointLabel})`
    }

    labelSpan.textContent = displayLabel
  }

  // Duplicate a column with a new column_ref
  duplicateColumn(event) {
    event.preventDefault()
    event.stopPropagation()

    const columnRef = event.params.columnRef
    logger.log('[Column Selector Panel] Duplicating column:', columnRef)

    const sourceItem = this.visibleListTarget.querySelector(`[data-column-ref="${columnRef}"]`)
    if (!sourceItem) {
      logger.error('[Column Selector Panel] Source column not found:', columnRef)
      return
    }

    // Generate new column_ref
    const columnName = sourceItem.dataset.columnName
    const newRef = `${columnName}_${Date.now()}`

    // Clone the item
    const clone = sourceItem.cloneNode(true)

    // Update clone with new column_ref
    clone.dataset.columnRef = newRef
    clone.dataset.currentParameters = '{}'  // Reset parameters
    clone.classList.remove('selected')

    // Update data attributes on buttons
    const duplicateBtn = clone.querySelector('.duplicate-button')
    if (duplicateBtn) {
      duplicateBtn.dataset.columnSelectorPanelColumnRefParam = newRef
    }
    const removeBtn = clone.querySelector('.remove-button')
    if (removeBtn) {
      removeBtn.dataset.columnRef = newRef
    }

    // Reset hidden field values
    clone.querySelectorAll('[data-parameter]').forEach(input => {
      input.value = ''
    })

    // Reset display label
    const labelSpan = clone.querySelector('.column-name')
    if (labelSpan) {
      labelSpan.textContent = sourceItem.dataset.columnLabel
    }

    // Insert after source
    sourceItem.after(clone)

    // Update empty state
    this.updateEmptyState()

    // Save
    this.saveOrder()

    // Select the new column
    setTimeout(() => {
      clone.click()
    }, 100)
  }
}
